import { ref } from 'vue';
import { useToast } from './useToast';
import { apiFetch } from '../services/api';
import { useEntitiesStore } from '../stores/entities';
import { field } from '../utils/wire';
import type { Device, DeviceValue, DeviceValueWithReferences } from '@/types';

// Property name variations for flexible matching
const PROPERTY_VARIATIONS: Record<string, string[]> = {
  onOff: ['on_off', 'onoff', 'state', 'power', 'enabled'],
  switch: ['on_off', 'switch', 'onoff', 'state', 'power'],
  brightness: ['brightness', 'dimming', 'level'],
  color: ['color', 'rgb'],
  colorTemperature: ['color_temp', 'color_temperature', 'temperature'],
  position: ['position', 'level', 'percentage'],
  targetTemperature: ['target_temperature', 'setpoint'],
  currentTemperature: ['current_temperature', 'temperature'],
  valveOutput: ['valve', 'valve_output', 'heating'],
  stop: ['stop'],
  upDown: ['move_up', 'move_down'],
  move_up: ['move_up'],
  move_down: ['move_down'],
  percentage: ['percentage', 'speed', 'level'],
  fan_speed: ['fan_speed'],
  fan_speed_step: ['fan_speed_step'],
  fan_speed_percentage: ['fan_speed_percentage', 'percentage', 'speed'],
  preset_mode: ['preset_mode'],
  oscillating: ['oscillating'],
  direction: ['direction'],
  unlock: ['state', 'power', 'on_off', 'unlock'],
  alarmState: ['state', 'alarm_state'],
  press: ['press'],
  target_humidity: ['target_humidity', 'humidity'],
  operation_mode: ['operation_mode'],
  controller_mode: ['controller_mode'],
  heat_cool: ['heat_cool'],
  setpoint_shift: ['setpoint_shift'],
  effective_target: ['effective_target', 'target_temperature', 'setpoint'],
  swing: ['swing'],
  swing_horizontal: ['swing_horizontal'],
  on_off: ['on_off', 'state', 'power'],
  humidity: ['humidity'],
};

/**
 * The device's value rows — the entities store owns that read, legacy `values`
 * alias included (`getDeviceValueRows`).
 */
function deviceValuesOf(device: Device): DeviceValueWithReferences[] {
  return useEntitiesStore().getDeviceValueRows(device) ?? [];
}

export function useDeviceControl() {
  const toast = useToast();
  const controlling = ref(false);

  /**
   * Find a device value by searching for matching property names
   */
  function findValueByKeys(device: Device, keys: string[]): DeviceValue | undefined {
    // Support both deviceValues (deserialized) and values (raw from server)
    const values = deviceValuesOf(device);
    if (!values.length) return undefined;

    // Pass 1: exact valueKey match (most specific)
    const byKey = values.find((v) => {
      const keyLower = (v.valueKey || '').toLowerCase();
      return keys.some((k) => keyLower === k.toLowerCase());
    });
    if (byKey) return byKey;

    // Pass 2: name substring match (fallback only when no key match)
    return values.find((v) => {
      const nameLower = (v.name || '').toLowerCase();
      return keys.some((k) => nameLower.includes(k.toLowerCase()));
    });
  }

  /**
   * Set a device property value via the control API
   */
  async function setProperty(device: Device, propertyName: string, value: unknown): Promise<boolean> {
    try {
      controlling.value = true;

      const values = deviceValuesOf(device);
      console.log('[useDeviceControl] setProperty called:', {
        deviceId: device.id,
        deviceName: device.deviceName,
        propertyName,
        value,
        valuesCount: values.length,
        hasDeviceValues: !!device.deviceValues,
        hasValues: !!field(device, 'values'),
      });

      // Special case: cover up/down selects distinct values
      let deviceValue: DeviceValue | undefined;
      if (propertyName === 'upDown') {
        const variant = value ? 'move_down' : 'move_up';
        deviceValue = findValueByKeys(device, [variant]);
      } else {
        // Find the device value for this property
        const searchKeys = PROPERTY_VARIATIONS[propertyName] || [propertyName];
        deviceValue = findValueByKeys(device, searchKeys);
      }

      if (!deviceValue) {
        const values = deviceValuesOf(device);
        console.error(`[useDeviceControl] No controllable value found for property: ${propertyName}`);
        console.log('[useDeviceControl] Available values:', values);
        console.log('[useDeviceControl] Search keys:', PROPERTY_VARIATIONS[propertyName] || [propertyName]);
        toast.error(`Cannot control ${propertyName}: Value not found on this device`);
        controlling.value = false;
        return false;
      }

      console.log('[useDeviceControl] Found device value:', deviceValue);

      // Use the device value control endpoint
      const path = `/devices/${device.id}/values/${deviceValue.valueKey}/control`;
      console.log('[useDeviceControl] Making request to:', path);
      console.log('[useDeviceControl] Request body:', { value });

      // Race the request against a timeout so `controlling` can never get
      // stuck when a device driver never acks (no fetch-level timeout exists).
      const CONTROL_TIMEOUT_MS = 10000;
      await Promise.race([
        apiFetch(path, {
          method: 'POST',
          body: JSON.stringify({ value }),
        }),
        new Promise<never>((_, reject) => {
          setTimeout(
            () => reject(new Error(`Request timed out after ${CONTROL_TIMEOUT_MS / 1000}s`)),
            CONTROL_TIMEOUT_MS,
          );
        }),
      ]);

      console.log('[useDeviceControl] Value set successfully (waiting for socket update)');
      return true;
    } catch (error) {
      console.error(`[useDeviceControl] Error setting ${propertyName}:`, error);
      toast.error(`Failed to set ${propertyName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    } finally {
      controlling.value = false;
    }
  }

  return {
    controlling,
    setProperty,
    findValueByKeys,
  };
}
